import rangeHandler from 'helpers/rangeHandler'
import config from 'services/config'

const state = getBasicState()

function getBasicState () {
    return {
        courseId: null,
        videoId: null,
        embedType: null,
        position: 0,
        videoDuration: 999999,
        startingSequences: [],
        currentSessionSequences: [],
        hash: '',
        moveTarget: null,
        demoWatchTimeLimit: 30,
        showOnlyDemo: true,
        videoProps: null,
        liveWatchedTime: 0,
        autoPlay: false,
        isRatingModalVisible: false,
        isLive: false
    }
}

const mutations = {
    VIDEO_PROGRESS (state, { currentPosition, currentSessionSequences, videoDuration }) {
        state.position = Math.round(currentPosition)
        state.currentSessionSequences = currentSessionSequences
        state.videoDuration = videoDuration
    },
    MOVE_VIDEO (state, target) {
        state.moveTarget = target
    },
    REFRESH_HASH (state, hash) {
        state.hash = hash
    },
    INITIATE_VIDEO_PLAYER (state, objectToAssign) {
        const stateToInitialize = { ...getBasicState(), ...objectToAssign }
        Object.keys(stateToInitialize).forEach((key) => {
            if (state[key] !== undefined) {
                state[key] = stateToInitialize[key]
            }
        })
    },
    CLEAR_VIDEO_PLAYER (state) {
        const basicState = getBasicState()
        Object.keys(basicState).forEach((key) => {
            state[key] = basicState[key]
        })
    },
    ASSIGN_LIVE_WATCH_TIME (state, time) {
        state.liveWatchedTime = time
    },
    SET_RATING_VISIBILITY (state, isRatingModalVisible) {
        state.isRatingModalVisible = isRatingModalVisible
    }
}

const actions = {
    initiateVideoPlayer (store, playerObject) {
        store.commit('INITIATE_VIDEO_PLAYER', playerObject)
        return Promise.resolve()
    },
    async clearVideoPlayer (store) {
        store.commit('CLEAR_VIDEO_PLAYER')
        await store.dispatch('VideoLog/clearLatestVideoLogEntry', null, { root: true })
        return Promise.resolve()
    },
    progressVideo (store, { currentPosition, currentSessionSequences, videoDuration, liveWatchedTime }) {
        store.commit('VIDEO_PROGRESS', { currentPosition, currentSessionSequences, videoDuration, liveWatchedTime })
        return Promise.resolve()
    },
    persistPlays (store) {
        const payload = {
            courseId: store.state.courseId,
            videoId: store.state.videoId,
            position: Math.round(store.state.position),
            watched: store.getters.totalWatchedAmount,
            durationSeconds: store.getters.videoDuration,
            timeRanges: store.getters.allWatchedSequences
        }
        const hash = JSON.stringify(payload)
        const userId = store.rootGetters['getUserId']

        if (hash !== store.hash && payload.courseId) {
            window.localStorage.setItem('videoLogs' + userId + store.state.courseId, hash)
            store.commit('REFRESH_HASH', payload)
        }
    },
    async sendVideoLogToAesir (store) {
        const userId = store.rootGetters['getUserId']
        const lastVideoLog = window.localStorage.getItem('videoLogs' + userId + store.state.courseId)

        if (lastVideoLog !== null) {
            const payload = JSON.parse(lastVideoLog)
            await store.dispatch('VideoLog/pushVideoLogForCourseAndVideo', payload, { root: true })

            return Promise.resolve()
        }
    },
    async sendVideoLogToAesirOnStart (store, videoLog) {
        const lastVideoLog = window.localStorage.getItem(videoLog)
        const payload = JSON.parse(lastVideoLog)
        await store.dispatch('VideoLog/pushVideoLogForCourseAndVideo', payload, { root: true })

        return Promise.resolve()
    },
    moveVideo (store, target) {
        store.commit('MOVE_VIDEO', target)
        return Promise.resolve()
    },
    updateLiveWatchedTime (store, time) {
        store.commit('ASSIGN_LIVE_WATCH_TIME', time)
        return Promise.resolve()
    },
    setRatingModalVisibility (store, isRatingModalVisible) {
        store.commit('SET_RATING_VISIBILITY', isRatingModalVisible)
    }
}

const getters = {
    totalWatchedAmount: (state, getters) => rangeHandler.sumRanges(getters.allWatchedSequences),
    allWatchedSequences: state => rangeHandler.unifyRanges(state.startingSequences, state.currentSessionSequences),
    position: state => state.position,
    videoDuration: state => state.videoDuration,
    moveTarget: state => state.moveTarget,
    embedType: state => state.embedType,
    videoProps: state => state.videoProps,
    isVimeo: state => state.embedType === 'VIMEO',
    isJW: state => state.embedType === 'JW',
    shouldInitializePlayer: (state, getters) => getters.videoProps !== null && (getters.isVimeo || getters.isJW),
    demoWatchTimeLimit: state => state.demoWatchTimeLimit,
    showOnlyDemo: state => state.showOnlyDemo,
    getLiveWatchedTime: state => state.liveWatchedTime,
    canUserSeeTest: (state, getters) => {
        if (state.courseId === null || getters.videoDuration === 0) {
            return false
        } else if (getters.videoDuration <= 10) {
            return (getters.totalWatchedAmount / getters.videoDuration) > 0.3
        } else {
            if (state.isLive) {
                return (getters.getLiveWatchedTime / getters.videoDuration) > 0.3
            }
            return (getters.totalWatchedAmount / getters.videoDuration) > +config.percentageVideoRequire
        }
    },
    getAmountWatched: (state, getters) => {
        if (state.courseId === null || getters.videoDuration === 0) {
            return false
        } else {
            return (getters.totalWatchedAmount / getters.videoDuration)
        }
    },
    shouldAutoPlay: (state) => state.autoPlay,
    getIsRatingModalVisible: state => state.isRatingModalVisible
}

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters
}
